import { faFile, faFileLines, faFilePdf } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { InboxAttachment } from '@spike/omnichannel-model';
import { MediaGalleryGrid } from 'components/UI/V3/MediaGalleryGrid/MediaGalleryGrid';
import { Media, MediaViewer } from 'components/UI/V3/MediaViewer/MediaViewer';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import styles from './MessageAttachments.module.scss';

interface MessageAttachmentsProps {
    attachments: Array<InboxAttachment>;
    /** Attachment URLs are signed and expire (1 h). Asks the thread to reload with fresh ones. */
    onExpired: () => void;
}

const isImage = (attachment: InboxAttachment): boolean => attachment.contentType.startsWith('image/');

const iconFor = (contentType: string) => {
    if (contentType.includes('pdf')) return faFilePdf;
    if (contentType.startsWith('text/') || contentType.includes('word') || contentType.includes('document')) {
        return faFileLines;
    }
    return faFile;
};

const humanSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const MessageAttachments: React.FC<MessageAttachmentsProps> = ({ attachments, onExpired }) => {
    const [viewerOpen, setViewerOpen] = useState(false);
    const [unavailable, setUnavailable] = useState(false);
    // one reload per set of URLs: a second failure on the same urls means the file is really gone
    const refreshedUrlsRef = useRef<string>('');

    const images = useMemo(() => attachments.filter(isImage), [attachments]);
    const files = useMemo(() => attachments.filter(attachment => !isImage(attachment)), [attachments]);

    const media = useMemo<Array<Media>>(
        () => images.map(image => ({ type: 'image' as const, src: image.url })),
        [images]
    );

    const urlsKey = useMemo(() => images.map(image => image.url).join('|'), [images]);

    /**
     * Capture phase so a failed thumbnail is seen here before the gallery swallows its own onError,
     * without touching the shared V3 component.
     */
    const handleLoadError = useCallback(() => {
        if (refreshedUrlsRef.current === urlsKey) {
            setUnavailable(true);
            return;
        }

        refreshedUrlsRef.current = urlsKey;
        onExpired();
    }, [onExpired, urlsKey]);

    const retry = useCallback(() => {
        refreshedUrlsRef.current = '';
        setUnavailable(false);
        onExpired();
    }, [onExpired]);

    if (attachments.length === 0) return null;

    return (
        <div className={styles.root}>
            {images.length > 0 &&
                (unavailable ? (
                    <div className={styles.unavailable}>
                        <span>Images could not be loaded.</span>
                        <button
                            type="button"
                            onClick={retry}
                        >
                            Retry
                        </button>
                    </div>
                ) : (
                    <div onErrorCapture={handleLoadError}>
                        <MediaGalleryGrid
                            media={images.map(image => ({ type: 'image' as const, url: image.url }))}
                            maxMobileColumns={2}
                            maxDesktopColumns={3}
                            className={styles.gallery}
                            onClick={() => setViewerOpen(true)}
                        />
                    </div>
                ))}

            {files.map(file => (
                <a
                    key={file.id}
                    href={file.url}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.file}
                    download={file.filename}
                >
                    <FontAwesomeIcon
                        icon={iconFor(file.contentType)}
                        className={styles.fileIcon}
                    />
                    <span className={styles.fileName}>{file.filename}</span>
                    <span className={styles.fileSize}>{humanSize(file.byteSize)}</span>
                </a>
            ))}

            {viewerOpen && media.length > 0 && (
                <MediaViewer
                    media={media}
                    onClose={() => setViewerOpen(false)}
                />
            )}
        </div>
    );
};

export default MessageAttachments;
